import { useEffect, useRef, useState } from "react";
import { TablePaginationConfig } from "antd";
import { FilterValue, SorterResult } from "antd/es/table/interface";

import { BaseEntity, QueryTableProps, QueryTableState } from "./types";
import BaseTable from "./BaseTable";
import { PAGINATION } from "@/shared/utils/constants";

import "./styles.css";

const QueryTable = <T extends BaseEntity, P extends BaseEntity = T>({
  query,
  filters = {},
  normalizeData,
  columns,
  pagination,
  onTableStateChange,
  ...props
}: QueryTableProps<T, P>) => {
  const defaultSortedColumn = columns.find((column) => column.defaultSortOrder);
  const [rows, setRows] = useState<P[]>([]);
  const defaultPagination = {
    defaultCurrent:
      (pagination && pagination.defaultCurrent) || PAGINATION.PAGE.DEFAULT,
    defaultPageSize:
      (pagination && pagination.defaultPageSize) ||
      PAGINATION.PAGE_SIZE.DEFAULT,
    pageSizeOptions: (() => {
      const defaultOptions = PAGINATION.PAGE_SIZE.QUERY_TABLE_OPTIONS;
      const pageSize = pagination && pagination.defaultPageSize;
      return pageSize && !defaultOptions.includes(pageSize)
        ? [...defaultOptions, pageSize].sort((a, b) => a - b)
        : defaultOptions;
    })(),
  };
  const [tableState, setTableState] = useState<QueryTableState>({
    pagination: {
      current: defaultPagination.defaultCurrent,
      pageSize: defaultPagination.defaultPageSize,
      pageSizeOptions: defaultPagination.pageSizeOptions,
      total: 0,
    },
    ordering: defaultSortedColumn
      ? `${defaultSortedColumn.defaultSortOrder === "descend" ? "-" : ""}${defaultSortedColumn.dataIndex}`
      : undefined,
  });
  const { pagination: currentPagination, ordering } = tableState;
  const [trigger, { data, error, isSuccess, isLoading, isFetching }] = query();
  const prevFiltersRef = useRef(filters);

  useEffect(() => {
    const filtersChanged =
      JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
    if (filtersChanged) {
      setTableState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          current: 1,
        },
      }));
    }
    prevFiltersRef.current = filters;

    trigger({
      page: filtersChanged
        ? PAGINATION.PAGE.DEFAULT
        : currentPagination.current,
      pageSize: currentPagination.pageSize,
      ordering,
      ...filters,
    });
  }, [
    currentPagination.current,
    currentPagination.pageSize,
    ordering,
    filters,
  ]);

  useEffect(() => {
    onTableStateChange?.({
      page: currentPagination.current,
      pageSize: currentPagination.pageSize,
      ordering,
    });
  }, [currentPagination.current, currentPagination.pageSize, ordering]);

  const isPastLastPage =
    currentPagination.current > 1 &&
    error !== undefined &&
    "status" in error &&
    error.status === 404;
  useEffect(() => {
    if (isPastLastPage) {
      setTableState((prev) => ({
        ...prev,
        pagination: { ...prev.pagination, current: 1 },
      }));
    }
  }, [isPastLastPage]);

  useEffect(() => {
    if (isSuccess && data?.results) {
      setRows(normalizeData ? normalizeData(data.results) : data.results);

      setTableState((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          total: data.count || 0,
        },
      }));
    }
  }, [data, isSuccess, normalizeData]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<P> | SorterResult<P>[],
  ) => {
    const singleSorter = !Array.isArray(sorter) ? sorter : null;
    setTableState({
      pagination: {
        current: pagination.current || defaultPagination.defaultCurrent,
        pageSize: pagination.pageSize || defaultPagination.defaultPageSize,
        pageSizeOptions:
          pagination.pageSizeOptions || defaultPagination.pageSizeOptions,
        total: tableState.pagination.total,
      },
      ordering: singleSorter?.field
        ? `${singleSorter.order === "descend" ? "-" : ""}${singleSorter.field}`
        : undefined,
    });
  };

  return (
    <BaseTable<P>
      className="query-table"
      {...props}
      columns={columns}
      dataSource={rows}
      rowKey={(record: P) => record.id}
      loading={{ spinning: isLoading || isFetching, size: "large" }}
      onChange={handleTableChange}
      pagination={tableState.pagination}
    />
  );
};

export default QueryTable;
